import { useCallback } from 'react';
import { useSearchParams } from 'react-router';

import { RecordingType } from '@/features/modules/types';

export const SEARCH_PARAM_KEY = 'recording_id';
export const SEARCH_PARAM_TYPE_KEY = 'recording_type';

// Backward compatibility - deprecated
export const SEARCH_PARAM_SESSION_KEY = 'recording_session_id';

export const useRecordingDrawer = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const open = useCallback(
    (id: string, type: RecordingType = 'session') => {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);

        // Clear old params for backward compatibility
        newParams.delete(SEARCH_PARAM_SESSION_KEY);

        // Set the unified recording ID and type
        newParams.set(SEARCH_PARAM_KEY, id);
        newParams.set(SEARCH_PARAM_TYPE_KEY, type);

        return newParams;
      });
    },
    [setSearchParams]
  );

  const close = useCallback(() => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      // Clear both new and old params
      newParams.delete(SEARCH_PARAM_KEY);
      newParams.delete(SEARCH_PARAM_TYPE_KEY);
      newParams.delete(SEARCH_PARAM_SESSION_KEY);
      return newParams;
    });
  }, [setSearchParams]);

  // Get current recording ID and type
  const recordingId = searchParams.get(SEARCH_PARAM_KEY);
  const recordingTypeParam = searchParams.get(SEARCH_PARAM_TYPE_KEY);

  // Backward compatibility: check old session key if new key not found
  const legacySessionId = searchParams.get(SEARCH_PARAM_SESSION_KEY);
  const currentRecordingId = recordingId ?? legacySessionId;

  // Determine recording type from URL params
  let currentRecordingType: RecordingType | null = null;
  if (recordingTypeParam === 'session' || recordingTypeParam === 'topic') {
    currentRecordingType = recordingTypeParam;
  } else if (legacySessionId) {
    // Backward compatibility: if using old key, assume it's a session
    currentRecordingType = 'session';
  }

  return {
    open,
    close,
    currentRecordingId,
    currentRecordingType,
    isOpen: !!currentRecordingId,
  };
};
